import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router";
import { useSession } from "@/lib/auth/session";
import { LoginForm } from "./LoginForm";
import { SchemaShowcase, WorkbookPlane } from "./SchemaShowcase";
import { ProlyMark } from "@/components/ProlyMark";
import "./login.css";

/** Human copy for the `?error=` codes the backend's Google callback sends. */
const ERROR_COPY: Record<string, string> = {
  AccessDenied: "That account can't sign in. Ask an admin for access.",
  OAuthState: "Sign-in expired. Please try again.",
  OAuthCallback: "Google sign-in failed. Please try again.",
  Configuration: "Google sign-in isn't configured. Use an email code instead.",
};

/** Same-site relative path only (mirrors the backend's safeNextPath). */
function safeNext(raw: string | null): string {
  return raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : "/";
}

export function LoginPage() {
  const session = useSession();
  const [params] = useSearchParams();
  const next = safeNext(params.get("callbackUrl"));
  const errorCode = params.get("error");
  const [googleEnabled, setGoogleEnabled] = useState(true);

  useEffect(() => {
    fetch("/api/auth/providers")
      .then((r) => (r.ok ? r.json() : null))
      .then((p: { google?: boolean } | null) => setGoogleEnabled(p?.google !== false))
      .catch(() => undefined);
  }, []);

  if (session.status === "authenticated") return <Navigate to={next} replace />;

  return (
    <div className="login-root relative min-h-screen w-full overflow-x-clip">
      {/* full-page animated workbook background */}
      <WorkbookPlane />

      <div className="relative z-10 grid min-h-screen lg:grid-cols-[minmax(0,0.78fr)_minmax(0,1.55fr)]">
        {/* ============================ Left: auth ========================== */}
        <section className="auth-glass relative flex flex-col px-6 py-10 sm:px-10 lg:px-10 xl:px-12">
          <div className="relative z-10 mx-auto flex w-full max-w-[400px] flex-1 flex-col">
            {/* brand */}
            <header className="rise flex items-center gap-3" style={{ animationDelay: "0.02s" }}>
              <ProlyMark size={54} radius={16} />
              <span className="brand-wordmark text-[25px] font-semibold text-[var(--p-ink)]">Proly</span>
            </header>

            {/* content */}
            <div className="pt-9">
              <p
                className="rise mb-3.5 inline-flex items-center gap-2 text-[14px] font-medium text-[var(--p-ink-soft)]"
                style={{ animationDelay: "0.08s" }}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--t-number)]" />
                Product Database &amp; Listing Management
              </p>

              <h1
                className="font-display-serif rise text-[34px] leading-[1.08] text-[var(--p-ink)] sm:text-[40px]"
                style={{ animationDelay: "0.12s" }}
              >
                Vendor sheets in,
                <br />
                <span className="italic text-[var(--p-accent-ink)]">Shopify-ready</span> products out.
              </h1>

              <p
                className="rise mt-4 text-[15.5px] leading-relaxed text-[var(--p-ink-soft)]"
                style={{ animationDelay: "0.18s" }}
              >
                Map any supplier spreadsheet to Shopify&apos;s product CSV once, reuse it as a
                template, and export clean, validated listings every time.
              </p>

              {/* auth controls */}
              <div className="rise mt-11" style={{ animationDelay: "0.24s" }}>
                {errorCode ? (
                  <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {ERROR_COPY[errorCode] ?? "Sign-in failed. Please try again."}
                  </p>
                ) : null}
                {googleEnabled ? (
                <>
                  <a
                    href={`/api/auth/signin/google?callbackUrl=${encodeURIComponent(next)}`}
                    className="p-btn-google flex w-full items-center justify-center gap-3 rounded-xl px-4 py-3 text-[14px] font-medium"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden>
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Continue with Google
                  </a>

                <div className="my-5 flex items-center gap-3">
                  <span className="h-px flex-1 bg-[var(--p-border)]" />
                  <span className="text-[11px] font-medium uppercase tracking-wider text-[var(--p-faint)]">
                    or with email
                  </span>
                  <span className="h-px flex-1 bg-[var(--p-border)]" />
                </div>
                </>
                ) : null}

                <LoginForm next={next} />
              </div>

            </div>
            {/* /content */}

            <footer
              className="rise mt-auto flex flex-col gap-1 pt-10 text-[12px] text-[var(--p-faint)]"
              style={{ animationDelay: "0.3s" }}
            >
              <span>By continuing you agree to Proly&apos;s Terms &amp; Privacy Policy.</span>
              <span>
                © Copyright{" "}
                <a
                  href="https://adfinite.com"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="font-medium text-[var(--p-ink-soft)] transition-colors hover:text-[var(--p-accent-ink)] hover:underline"
                >
                  Adfinite Solutions LLP
                </a>
              </span>
            </footer>
          </div>
        </section>

        {/* ========================= Right: showcase ======================= */}
        <section className="relative hidden overflow-hidden lg:block">
          <SchemaShowcase />
        </section>
      </div>
    </div>
  );
}
